import { useSelector } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';
import type { RootState } from '../grid/reduxStore/Store';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { useCurrentUser } from '../../../providers/UserProvider';
import type { BlockSettings, ShapeType } from '../grid/reduxStore/blockStyleSettingsSlice';
import { addBlockToTemporaryStyle, clearTemporaryStyleForBlock } from '../grid/reduxStore/blockStyleSettingsSlice';
import {
  addBlockSettings,
  deleteBlockSettings,
  updateBlockSettings,
  setActiveSidePanel,
  EditorDefaultSidePanelType,
} from '../grid/reduxStore/blockStyleSettingsSlice';
import { BlockSides } from '../GridDndEditor/Block/Helper/BlockSides';
import { useNewBlockStylesChangedHandler } from './useNewBlockStylesChangedHandler.ts';
import {
  DEFAULT_BLOCK_BACKGROUND_COLOR,
  DEFAULT_BLOCK_BORDER_COLOR,
  DEFAULT_SHAPE_BACKGROUND_COLOR,
} from '../shared/documentColorDefaults';

const initialBorderSettings = {
  borderLeft: 0,
  borderRight: 0,
  borderTop: 0,
  borderBottom: 0,
};

export const defaultBlockStyle: BlockSettings = {
  blockId: '',
  updatedByUserId: 0,
  lockedBy: null,
  borderRadius: 0,
  borderColor: DEFAULT_BLOCK_BORDER_COLOR,
  backgroundColor: DEFAULT_BLOCK_BACKGROUND_COLOR,
  opacity: 100,
  imageAlt: '',
  imageLink: '',
  borderWidth: 0,
  currentBorderSide: [BlockSides.ALL_SIDES],
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  imageAspectRatioLock: true,
  ...initialBorderSettings,
};
export const shapeDefaultStyle = { ...defaultBlockStyle, backgroundColor: DEFAULT_SHAPE_BACKGROUND_COLOR, imageAspectRatioLock: false };

export const useNewBlockStyleSettings = (blockId: string) => {
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser.id;
  const originalStateRef = useRef<BlockSettings>();
  const initialBlockStyle = { ...defaultBlockStyle, updatedByUserId: userId };
  const isCommittingRef = useRef(false);

  const blockStylesChangedHandler = useNewBlockStylesChangedHandler();

  const dispatch = useAppDispatch();
  const blocksSettings = useSelector((state: RootState) => state.blockStyleSettings);
  const latestBlocksSettingsRef = useRef(blocksSettings);
  const latestHandlerRef = useRef(blockStylesChangedHandler);

  const saveCurrentStateToRef = (style: BlockSettings) => {
    originalStateRef.current = { ...style };
  };

  const getBlockStyle = useCallback(
    (blockId) => {
      if (blocksSettings.blockStyle[blockId]) {
        return blocksSettings.blockStyle[blockId];
      }
      return { ...initialBlockStyle };
    },
    [blocksSettings.blockStyle, blockId, initialBlockStyle]
  );

  useEffect(() => {
    latestBlocksSettingsRef.current = blocksSettings;
  }, [blocksSettings]);

  useEffect(() => {
    latestHandlerRef.current = blockStylesChangedHandler;
  }, [blockStylesChangedHandler]);

  useEffect(() => {
    return () => {
      if (isCommittingRef.current) return;

      const tempStyle = latestBlocksSettingsRef.current.temporaryStyle[blockId];
      if (tempStyle) {
        const { originalStyle, dirtyStyle } = tempStyle;
        const hasChanged = JSON.stringify(originalStyle) !== JSON.stringify(dirtyStyle);

        if (hasChanged) {
          latestHandlerRef.current(blockId, originalStyle, dirtyStyle);
        }
        dispatch(clearTemporaryStyleForBlock({ blockId }));
        originalStateRef.current = undefined;
      }
    };
  }, [blockId, dispatch]);

  useEffect(() => {
    !blockId && dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  }, [blockId, dispatch]);

  const getActiveBorderSides = (previousStyles: BlockSettings, borderWidth: number) => {
    const blockSettings = { ...previousStyles };
    const activeBorderSides = blockSettings.currentBorderSide ?? [BlockSides.ALL_SIDES];
    if (activeBorderSides.includes(BlockSides.LEFT) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderLeft = borderWidth;
    }
    if (activeBorderSides.includes(BlockSides.RIGHT) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderRight = borderWidth;
    }
    if (activeBorderSides.includes(BlockSides.TOP) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderTop = borderWidth;
    }
    if (activeBorderSides.includes(BlockSides.BOTTOM) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderBottom = borderWidth;
    }
    return { ...blockSettings, borderWidth };
  };

  const addBlock = (blockId: string | null, isShapeBlockType?: boolean) => {
    if (blockId && !blocksSettings.blockStyle[blockId]) {
      const defaultAddedBlockSettings = {
        ...defaultBlockStyle,
        blockId,
      };
      const shapeAddedBlockSettings = {
        ...shapeDefaultStyle,
        blockId,
      };
      const addedBlockSettings = isShapeBlockType ? shapeAddedBlockSettings : defaultAddedBlockSettings;
      const payload = { blockId: blockId || '', blockSettings: addedBlockSettings };
      dispatch(addBlockSettings(payload));
    }
  };

  const deleteBlock = (blockId: string | null) => {
    blockId && dispatch(deleteBlockSettings({ blockId }));
  };

  const updateBlockStyleInUi = (style: Partial<BlockSettings>) => {
    const currentStyle = getBlockStyle(blockId);
    if (!blockId) return;

    const newStyle = { ...currentStyle, ...style };

    dispatch(updateBlockSettings({ blockId, blockSettings: newStyle }));

    if (originalStateRef.current) {
      dispatch(
        addBlockToTemporaryStyle({
          blockId,
          originalStyle: originalStateRef.current,
          dirtyStyle: newStyle,
        })
      );
    }
  };

  const updateBlockStyleWithUndo = async (style: Partial<BlockSettings>) => {
    const firstStyleKey = Object.keys(style)[0];
    if (!blockId || style[firstStyleKey] === originalStateRef.current?.[firstStyleKey]) return;

    const previousStyles = originalStateRef.current || getBlockStyle(blockId);
    const newStyle = { ...previousStyles, ...style };

    await blockStylesChangedHandler(blockId, previousStyles, newStyle);

    originalStateRef.current = undefined;
    dispatch(clearTemporaryStyleForBlock({ blockId }));
  };

  const saveCurrentBlockStyle = async (style: BlockSettings) => {
    const previousStyles = { ...originalStateRef.current, blockId, updatedByUserId: userId };

    const newStyle: BlockSettings = { ...previousStyles, ...style };
    await blockStylesChangedHandler(style.blockId, previousStyles, newStyle);
  };

  const changeBlockBorderSide = async (sides: BlockSides[]) => {
    if (!blockId) return;

    let updatedCurrentBlockStyle: BlockSettings = { ...blocksSettings.blockStyle[blockId] };
    const { borderWidth } = updatedCurrentBlockStyle;
    const activeBorderSettings = {};

    const beforeState = { ...updatedCurrentBlockStyle };

    if (!sides.length) {
      updatedCurrentBlockStyle = { ...updatedCurrentBlockStyle, ...initialBorderSettings };
    } else {
      sides.forEach((side: BlockSides) => {
        const borderName = `border${side}`;
        activeBorderSettings[borderName] = borderWidth;
      });
      updatedCurrentBlockStyle = { ...getBlockStyle(blockId), ...initialBorderSettings, ...activeBorderSettings };
    }

    const newStyle = {
      ...updatedCurrentBlockStyle,
      ...initialBorderSettings,
      ...activeBorderSettings,
      currentBorderSide: sides,
    };

    await blockStylesChangedHandler(blockId, beforeState, newStyle);
  };

  const changeBorderRadiusInUi = async ({ radius, toAddPadding = false }: { radius: number; toAddPadding?: boolean }) => {
    if (!blockId) return;

    const previousStyles = { ...originalStateRef.current, blockId, updatedByUserId: userId };
    const calculatedPadding = toAddPadding ? radius / 20 : 0;
    const newStyle = {
      ...previousStyles,
      borderRadius: radius,
      paddingLeft: calculatedPadding,
      paddingTop: calculatedPadding,
      paddingRight: calculatedPadding,
      paddingBottom: calculatedPadding,
    };

    dispatch(
      updateBlockSettings({
        blockId,
        blockSettings: newStyle,
      })
    );
    dispatch(
      addBlockToTemporaryStyle({
        blockId,
        originalStyle: previousStyles,
        dirtyStyle: newStyle,
      })
    );
  };

  const changeBorderRadius = async ({ radius, toAddPadding = false }: { radius: number; toAddPadding?: boolean }) => {
    if (!blockId || radius === originalStateRef.current?.borderRadius) return;
    isCommittingRef.current = true;

    const previousStyles = { ...originalStateRef.current, blockId, updatedByUserId: userId };
    const calculatedPadding = toAddPadding ? radius / 20 : 0;
    const newStyle = {
      ...previousStyles,
      borderRadius: radius,
      paddingLeft: calculatedPadding,
      paddingTop: calculatedPadding,
      paddingRight: calculatedPadding,
      paddingBottom: calculatedPadding,
    };

    await blockStylesChangedHandler(blockId, previousStyles, newStyle);
    dispatch(clearTemporaryStyleForBlock({ blockId }));
  };

  const changeShapeType = async (type: ShapeType, isBorderRadiusShouldBeReset: boolean) => {
    if (!blockId) return;

    const previousStyles = { ...originalStateRef.current, blockId, updatedByUserId: userId };
    const newStyle = {
      ...previousStyles,
      borderRadius: isBorderRadiusShouldBeReset ? 0 : previousStyles.borderRadius,
    };

    await blockStylesChangedHandler(blockId, previousStyles, newStyle);
  };

  const changeBorderWidthInUi = async (width: number) => {
    if (!blockId) return;

    const previousStyles = { ...originalStateRef.current, blockId, updatedByUserId: userId };
    const newStyles: BlockSettings = getActiveBorderSides(previousStyles, width);
    dispatch(
      updateBlockSettings({
        blockId,
        blockSettings: newStyles,
      })
    );
    dispatch(
      addBlockToTemporaryStyle({
        blockId,
        originalStyle: previousStyles,
        dirtyStyle: newStyles,
      })
    );
  };

  const changeBorderWidth = async (borderWidth: number) => {
    if (!blockId || borderWidth === originalStateRef.current?.borderWidth) return;

    const previousStyles = { ...originalStateRef.current, blockId, updatedByUserId: userId };
    const newStyles: BlockSettings = getActiveBorderSides(previousStyles, borderWidth);
    await blockStylesChangedHandler(blockId, previousStyles, newStyles);
    dispatch(clearTemporaryStyleForBlock({ blockId }));
  };

  const saveCopiedBlockStyle = async (blockId: string, style: BlockSettings) => {
    await blockStylesChangedHandler(blockId, style, style);
  };

  const getBlockStyleById = (blockId: string): BlockSettings | undefined => {
    return blocksSettings.blockStyle[blockId];
  };

  return {
    getBlockStyle,
    getBlockStyleById,
    addBlock,
    deleteBlock,
    changeBlockBorderSide,
    changeBorderRadius,
    changeBorderWidth,
    saveCurrentBlockStyle,
    saveCopiedBlockStyle,
    changeShapeType,
    saveCurrentStateToRef,
    updateBlockStyleInUi,
    changeBorderWidthInUi,
    updateBlockStyleWithUndo,
    changeBorderRadiusInUi,
  };
};
